import { Button } from "@chakra-ui/button";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import CommentsContextProvider from "../components/CommentsContextProvider";
import FormSkeletonLoader from "../components/common-quote/FormSkeletonLoader";
import FormStaleQuoteDialog from "../components/common-quote/FormStaleQuoteDialog";
import QuoteContextProvider from "../components/common-quote/QuoteContextProvider";
import FixAndFlipQuoteForm from "../components/quote/FixAndFlipQuoteForm";
import RentalQuoteForm from "../components/quote/RentalQuoteForm";
import RouteLeavingGuard from "../components/RouteLeavingGuard";
import useQuery from "../hooks/useQuery";
import useQuote from "../hooks/useQuote";
import { QuoteTypeEnum } from "../lib/models/enums";
import { QuoteType } from "../lib/types/QuoteType";

function QuoteView() {
  const query = useQuery();
  const { id } = useParams<any>();
  const [quoteType, setQuoteType] = React.useState<QuoteType | undefined>(
    undefined
  );

  useEffect(() => {
    const quoteParams = query.get("type")?.toLowerCase();

    if (!quoteParams) return;

    if (quoteParams === QuoteTypeEnum.stf) {
      setQuoteType("STF");
    } else if (quoteParams === QuoteTypeEnum.rental) {
      setQuoteType("Rental");
    } else {
      setQuoteType("Premier");
    }
  }, [id, query]);

  return (
    <>
      {quoteType && (
        <QuoteContextProvider id={id} quoteType={quoteType}>
          <CommentsContextProvider id={id}>
            <QuoteConsumer />
          </CommentsContextProvider>
        </QuoteContextProvider>
      )}
    </>
  );
}

function QuoteConsumer() {
  const history = useHistory();
  const quoteContext = useQuote();

  const {
    state,
    saveQuoteHelper,
    calculateQuote,
    quoteType,
    saveQuoteMetrics,
    submitQuoteHandler,
    deleteQuote,
    quoteId,
  } = quoteContext;
  const { metadata } = state;

  const shouldBlockNavigation = (location: Location) => {
    const { location: locationFromHistory } = history;
    const { pathname: currentPathname } = locationFromHistory;

    if (location.pathname === currentPathname) {
      return false;
    }

    return true;
  };

  const handleBackClick = () => {
    history.location.state === undefined
      ? history.push("/quotes/my-quotes")
      : history.goBack();
  };

  return (
    <Box marginTop={4}>
      {state.isLoading ? (
        <FormSkeletonLoader />
      ) : (
        <>
          <Button
            onClick={handleBackClick}
            leftIcon={<ArrowBackIcon />}
            variant="link"
            size="sm"
            color="brand.200"
            marginBottom={4}
          >
            Back to Quotes
          </Button>

          {quoteType === "Rental" || quoteType === "Premier" ? (
            <RentalQuoteForm
              quote={state.quote}
              submitHandler={submitQuoteHandler}
              saveHandler={saveQuoteHelper}
              saveQuoteMetrics={saveQuoteMetrics}
              calculateHandler={calculateQuote}
              deleteHandler={deleteQuote}
              isCalculating={state.isCalculating}
              isLoading={state.isLoading}
              requiresRecalculation={state.requiresRecalculation}
              quoteId={quoteId}
            />
          ) : (
            <FixAndFlipQuoteForm
              quote={state.quote}
              submitHandler={submitQuoteHandler}
              saveHandler={saveQuoteHelper}
              saveQuoteMetrics={saveQuoteMetrics}
              calculateHandler={calculateQuote}
              deleteHandler={deleteQuote}
              isCalculating={state.isCalculating}
              isLoading={state.isLoading}
              quoteId={quoteId}
            />
          )}
          <RouteLeavingGuard
            when={
              state.isDirty ||
              !state.hasCalculated ||
              state.requiresQuoteResubmission
            }
            shouldBlockNavigation={shouldBlockNavigation}
            navigate={(path) => history.push(path)}
          />
          <FormStaleQuoteDialog
            quote={state.quote}
            metadata={metadata}
            calculateHandler={calculateQuote}
            calculation={state.quoteCalculation}
          />
        </>
      )}
    </Box>
  );
}

export default QuoteView;
